import { useState } from 'react';
import { useApp } from '../../../contexts/AppContext';
import { Link } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import Button from '../../ui/Button';
import Modal from '../../ui/Modal';
import { useToast } from '../../ui/Toast';
import { haptics } from '../../../utils/haptics';
import { linkStationGate, planErrorMessage } from '../../../billing/pricingV2';
import { usePricingV2 } from '../../../hooks/usePricingV2';

export default function LinkAgentDialog({ isOpen, onClose }) {
  const { state, linkAgent } = useApp();
  const toast = useToast();
  const { plan } = usePricingV2();
  const gate = linkStationGate(plan, state.linkedAgents.length);
  const [agentId, setAgentId] = useState('');
  const [description, setDescription] = useState('');
  const [isLinking, setIsLinking] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!gate.allowed) {
      toast.warning(gate.message);
      return;
    }

    if (!agentId.trim()) {
      toast.warning('Please enter a Media Station ID');
      return;
    }

    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(agentId.trim())) {
      toast.error('Invalid Media Station ID format. Must be a valid UUID.');
      return;
    }

    setIsLinking(true);
    try {
      await linkAgent(agentId.trim(), description.trim() || 'My Media Station');
      toast.success('Media Station linked successfully!');
      haptics.success();
      setAgentId('');
      setDescription('');
      onClose();
    } catch (error) {
      toast.error(planErrorMessage(error, plan) ?? `Failed to link Media Station: ${error.message}`);
      haptics.error();
    } finally {
      setIsLinking(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Link Media Station" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {!gate.allowed && (
          <p
            data-testid="station-limit-notice"
            className="px-4 py-3 squircle-sm bg-amber-50 dark:bg-amber-900/20 text-body-sm text-amber-800 dark:text-amber-300"
          >
            {gate.message}
          </p>
        )}

        <div>
          <label className="block text-body-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Media Station ID *
          </label>
          <input
            type="text"
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            className="w-full px-4 py-3 squircle-sm bg-gray-100 dark:bg-elevated-dark-2 border-0 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 font-mono text-body-sm"
            disabled={isLinking}
          />
          <p className="text-caption text-gray-500 dark:text-gray-400 mt-2">
            Find this ID in the Media Station app's settings.{' '}
            <RouterLink
              to="/download"
              onClick={onClose}
              data-testid="link-dialog-download-link"
              className="font-medium text-primary-600 dark:text-primary-400 hover:underline"
            >
              Download Media Station
            </RouterLink>
          </p>
        </div>

        <div>
          <label className="block text-body-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Description (optional)
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g., Primary Media Center, Youth Group Hall"
            className="w-full px-4 py-3 squircle-sm bg-gray-100 dark:bg-elevated-dark-2 border-0 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 text-body-sm"
            disabled={isLinking}
          />
        </div>

        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            onClick={onClose}
            variant="secondary"
            className="flex-1"
            disabled={isLinking}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            className="flex-1"
            loading={isLinking}
            disabled={isLinking || !gate.allowed}
          >
            <Link className="w-4 h-4 mr-2" />
            Link Media Station
          </Button>
        </div>
      </form>
    </Modal>
  );
}
